import { ChangeDetectionStrategy, Component, effect, input, output, signal } from '@angular/core';

import { AbstractControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { InputMask } from 'primeng/inputmask';
import { Button } from 'primeng/button';
import { CardContent } from '@/shared/components/card-content/card-content';
import { DialogModule } from 'primeng/dialog';
import { VisualizadorPdf } from '@/shared/components/visualizador-pdf/visualizador-pdf';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { DateUtils } from '@/shared/utils/date-utils';
import { SwitchSimNao } from '@/shared/components/switch-sim-nao/switch-sim-nao';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({
    selector: 'app-formacao-graduacao',
    standalone: true,
    imports: [DialogoPadraoDirective, SwitchSimNao, ReactiveFormsModule, Select, InputText, InputMask, Button, CardContent, DialogModule, VisualizadorPdf, CanDirective],
    templateUrl: './formacao-graduacao.component.html',
    styleUrl: './formacao-graduacao.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Formulário de graduação (usado no diálogo de registro de formação). */
export class FormacaoGraduacaoComponent {
    form = input.required<FormGroup>();
    modoEdicao = input<boolean>(false);
    tipoFormacaoOptions = input<Array<{ label: string; value: number }>>([]);
    comprovanteGraduacaoOptions = input<Array<{ label: string; value: number }>>([]);
    tiposFormacaoCarregando = input<boolean>(false);
    diplomasCarregando = input<boolean>(false);
    prazoDiploma = input<string>('');
    paisesOptions = input<Array<{ label: string; value: number }>>([]);
    paisesCarregando = input<boolean>(false);
    prorrogarPrazoCarregando = input<boolean>(false);
    exibirBotaoProrrogarPrazo = input<boolean>(true);

    pesquisarEscola = output<void>();
    prorrogarPrazoDiploma = output<void>();

    protected readonly roleFormacaoProrrogar = PERMISSOES.medicos.formacao.prorrogarPrazo;

    exibirDialogAnexo = signal(false);
    blobAnexo = signal<Blob | null>(null);
    nomeArquivoAnexo = 'documento-comprobatorio.pdf';

    /** Tamanho máximo (Formacao.TX_CARGA_HORARIA) do campo numérico Carga Horária. */
    protected readonly cargaHorariaMaxLength = 6;

    /** Limite de caracteres por campo de texto, alinhado às colunas do backend (`@Column(length = ...)`). */
    private readonly limitesTexto: Readonly<Record<string, number>> = {
        nomeEscola: 70,
        nomeAnteriorEscola: 100,
        numeroProcessoDiploma: 20,
        tese: 300,
        livroRegistroDiploma: 20,
        numeroFolhaRegistro: 20,
        numeroRegistroMEC: 25,
        nomeCurso: 300,
        ufPrestadorIes: 2,
        nomeFaculdadeEstrangeira: 100,
        urlDocumentosComprobatorios: 500,
        codigoValidacaoDiploma: 200,
        urlValidacaoDiploma: 100,
        nomeXmlDiploma: 100,
        nomeRelatorioDiploma: 100,
        nomeRepresentacaoGraficaDiploma: 100
    };

    /** Campos de data cobertos pelo validador cruzado de período do curso. */
    private readonly camposPeriodoCurso: ReadonlySet<string> = new Set(['dataInicioCurso', 'dataConclusaoCurso']);

    /** FormGroups já configurados, evitando registrar os validadores mais de uma vez por instância. */
    private readonly formulariosConfigurados = new WeakSet<FormGroup>();

    constructor() {
        // O FormGroup é construído no diálogo pai; registra aqui as validações desta aba assim que ele chega/muda.
        effect(() => {
            const form = this.form();
            if (this.formulariosConfigurados.has(form)) return;
            this.formulariosConfigurados.add(form);
            this.registrarValidacoes(form);
        });
    }

    private registrarValidacoes(form: FormGroup): void {
        form.addValidators(this.periodoCursoValidator());

        const cargaHoraria = form.get('cargaHoraria');
        if (cargaHoraria) {
            cargaHoraria.addValidators([this.somenteDigitosValidator(), Validators.maxLength(this.cargaHorariaMaxLength)]);
            cargaHoraria.updateValueAndValidity({ emitEvent: false });
        }

        Object.entries(this.limitesTexto).forEach(([nome, limite]) => {
            const controle = form.get(nome);
            if (!controle) return;
            controle.addValidators(Validators.maxLength(limite));
            controle.updateValueAndValidity({ emitEvent: false });
        });

        form.updateValueAndValidity({ emitEvent: false });
    }

    /** Período do curso: rejeita data de início posterior à data fim (início ≤ fim). */
    private periodoCursoValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const dataInicio = String(control.get('dataInicioCurso')?.value ?? '').trim();
            const dataFim = String(control.get('dataConclusaoCurso')?.value ?? '').trim();

            if (!dataInicio || !dataFim) return null;
            if (!DateUtils.isDataValida(dataInicio) || !DateUtils.isDataValida(dataFim)) return null;

            const inicio = this.converterDataBr(dataInicio);
            const fim = this.converterDataBr(dataFim);

            if (!inicio || !fim) return null;

            return inicio.getTime() > fim.getTime() ? { periodoCursoInvalido: true } : null;
        };
    }

    /** Aceita somente dígitos (0-9); ignora campo vazio. */
    private somenteDigitosValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = String(control.value ?? '').trim();
            if (!valor) return null;

            return /^\d+$/.test(valor) ? null : { somenteDigitos: true };
        };
    }

    private converterDataBr(valor: string): Date | null {
        const [dia, mes, ano] = valor.split('/').map((parte) => Number(parte));

        if (!dia || !mes || !ano) return null;

        return new Date(ano, mes - 1, dia);
    }

    /** Remove caracteres não numéricos digitados ou colados na Carga Horária. */
    onCargaHorariaInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const somenteDigitos = input.value.replace(/\D/g, '');
        if (input.value !== somenteDigitos) {
            this.form().get('cargaHoraria')?.setValue(somenteDigitos);
        }
    }

    async abrirDocumentoComprobatorio(): Promise<void> {
        const url = String(this.form().get('urlDocumentosComprobatorios')?.value ?? '').trim();
        if (!url) return;

        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error(`Falha HTTP: ${response.status}`);

            const blob = await response.blob();
            if ((blob.type || '').includes('pdf') || !blob.type) {
                this.blobAnexo.set(blob.type ? blob : new Blob([blob], { type: 'application/pdf' }));
                this.exibirDialogAnexo.set(true);
                return;
            }
        } catch {
            // fallback abaixo
        }

        window.open(url, '_blank', 'noopener,noreferrer');
    }

    fecharDialogAnexo(): void {
        this.exibirDialogAnexo.set(false);
        this.blobAnexo.set(null);
    }

    campoInvalido(nomeCampo: string): boolean {
        const campo = this.form().get(nomeCampo);
        const periodoInvalido = this.camposPeriodoCurso.has(nomeCampo) && this.form().hasError('periodoCursoInvalido');
        return !!campo && (campo.invalid || periodoInvalido) && (campo.touched || campo.dirty);
    }

    mensagemErroCampo(nomeCampo: string): string | null {
        const campo = this.form().get(nomeCampo);

        if (this.camposPeriodoCurso.has(nomeCampo) && this.form().hasError('periodoCursoInvalido')) {
            return 'A data de início não pode ser superior à data fim.';
        }

        if (!campo?.errors) return null;

        if (campo.errors['required']) return 'Campo obrigatório.';
        if (campo.errors['dataInvalida']) return 'Data inválida.';
        if (campo.errors['dataMaiorQueHojeInvalida']) return 'A data deve ser menor ou igual a hoje.';
        if (campo.errors['somenteDigitos']) return 'Informe apenas números.';
        if (campo.errors['maxlength']) {
            const limite = nomeCampo === 'cargaHoraria' ? this.cargaHorariaMaxLength : (this.limitesTexto[nomeCampo] ?? 0);
            return `Máximo de ${limite} caracteres.`;
        }
        return 'Valor inválido.';
    }
}
